import { ChangeDetectionStrategy, Component, OnInit, computed, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';
import { DocsService } from '../../../core/docs/docs.service';
import { NotificationsService } from '../../../core/http/notifications.service';
import { UserService } from '../../../core/auth/user.service';
import { IconComponent } from '../../ui/icon/icon.component';
import { ThemeToggleComponent } from '../../ui/theme-toggle/theme-toggle.component';
import { TenantChipComponent } from '../../ui/tenant-chip/tenant-chip.component';

interface NavItem {
  label: string;
  icon: string;
  path: string;
  adminOnly?: boolean;
}

/**
 * App shell — gradient brand header + glass sidebar. Replaces HeaderComponent.
 * Projects the routed page into <main>.
 */
@Component({
  selector: 'app-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, IconComponent, ThemeToggleComponent, TenantChipComponent],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly docs = inject(DocsService);
  private readonly notifications = inject(NotificationsService);

  /**
   * odoo-5133 — pastille sur « Aide » tant que la derniere livraison decrite dans les notes
   * de version n'a pas ete ouverte. Publier une version dont personne n'apprend l'existence
   * revient a ne pas la publier : le testeur et les administrateurs clients decouvraient les
   * changements en butant dessus.
   */
  protected readonly nouveautesNonLues = this.docs.nouveautesNonLues;

  protected readonly username = computed(() => this.userService.username() || this.auth.username || '');
  protected readonly tenantName = computed(() => this.userService.tenantDisplayName() || 'Cover3D');
  protected readonly isAdmin = computed(() => this.userService.isAdmin());
  protected readonly initials = computed(() => {
    const u = this.username();
    if (!u) return '–';
    const parts = u.replace(/@.*$/, '').split(/[.\-_ ]+/).filter(Boolean);
    return (parts[0]?.[0] ?? '').concat(parts[1]?.[0] ?? '').toUpperCase() || u.slice(0, 2).toUpperCase();
  });

  protected readonly currentLang: 'fr' | 'en' =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/en') ? 'en' : 'fr';

  protected readonly navItems: NavItem[] = [
    { label: $localize`:@@nav.updates:Updates`, icon: 'sync', path: '/updates' },
    { label: $localize`:@@nav.licenses:Licences`, icon: 'key', path: '/licenses' },
    { label: $localize`:@@nav.licenseGroups:Groupes`, icon: 'workspaces', path: '/licensegroups' },
    { label: $localize`:@@nav.licenseDirectory:Annuaire`, icon: 'contacts', path: '/license-directory', adminOnly: true },
    { label: $localize`:@@nav.telemetry:Postes`, icon: 'computer', path: '/telemetry' },
    { label: $localize`:@@nav.packages:Packages`, icon: 'deployed_code', path: '/packages' },
    { label: $localize`:@@nav.help:Aide`, icon: 'help', path: '/aide' },
  ];

  protected readonly visibleNav = computed(() =>
    this.navItems.filter((i) => !i.adminOnly || this.isAdmin()),
  );

  ngOnInit(): void {
    // Les notes sont lues au demarrage pour savoir s'il y a du nouveau — un fichier de
    // quelques kilo-octets, servi avec le site, et le meme flux que celui de la page.
    this.docs.surveillerNouveautes();

    // Une invitation par session de navigation, pas une par changement de page ; la
    // pastille, elle, reste allumee jusqu'a la lecture.
    queueMicrotask(() => {
      if (this.docs.doitAnnoncerDansCetteSession()) {
        this.notifications.showInfo(
          $localize`:@@shell.newsToast:Des nouveautés sont disponibles — voir Aide.`,
        );
      }
    });
  }

  protected switchLanguage(): void {
    const target = this.currentLang === 'fr' ? 'en' : 'fr';
    const rest = window.location.pathname.replace(/^\/(en|fr)/, '');
    window.location.href = `/${target}${rest}${window.location.search}${window.location.hash}`;
  }

  protected logout(): void {
    this.auth.logout();
  }
}
